/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import type { RequestConfig, ResponseErrorConfig } from '../.kubb/client'
import type {
  TerminateTradingChallengeParticipationOptions,
  TerminateTradingChallengeParticipationStatus200,
  TerminateTradingChallengeParticipationStatus403,
  TerminateTradingChallengeParticipationStatus404,
  TerminateTradingChallengeParticipationStatus409,
  TerminateTradingChallengeParticipationStatus500,
} from '../types/TerminateTradingChallengeParticipation'
import type { UseMutationOptions, UseMutationResult, QueryClient } from '@tanstack/react-query'
import { terminateTradingChallengeParticipation } from '../clients/terminateTradingChallengeParticipation'
import { mutationOptions, useMutation } from '@tanstack/react-query'

export const terminateTradingChallengeParticipationMutationKey = () =>
  [{ url: '/v1/trading_challenges/:trading_challenge_id/participants/:user_id/terminate' }] as const

export function terminateTradingChallengeParticipationMutationOptions<TContext = unknown>(
  config: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>> = {},
) {
  const mutationKey = terminateTradingChallengeParticipationMutationKey()
  return mutationOptions<
    TerminateTradingChallengeParticipationStatus200,
    ResponseErrorConfig<
      | TerminateTradingChallengeParticipationStatus403
      | TerminateTradingChallengeParticipationStatus404
      | TerminateTradingChallengeParticipationStatus409
      | TerminateTradingChallengeParticipationStatus500
    >,
    TerminateTradingChallengeParticipationOptions,
    TContext
  >({
    mutationKey,
    mutationFn: async ({ path }) => {
      return terminateTradingChallengeParticipation({
        ...config,
        path,
        throwOnError: true,
      }).unwrap()
    },
  })
}

/**
 * @description End a participant's run in a challenge before its own rules would: the participant leaves, or an operator removes them. No prize is paid, even to a participant who could have claimed one -- claim the prize first if that is what you want. The account is wound down, every remaining challenge credit is swept, and the participation is marked TERMINATED and frozen: from then on it takes no further daily snapshots and never appears in the results ranking again. The user is left deactivated with no challenge balance, and is free to register for another challenge. Participants may only terminate their own run; terminating someone else's requires admin, integrator (same tenant) or challenge manager (assigned challenge) rights.
 * @summary Terminate a participation in a trading challenge
 * {@link /v1/trading_challenges/:trading_challenge_id/participants/:user_id/terminate}
 */
export function useTerminateTradingChallengeParticipation<TContext>(
  options: {
    mutation?: UseMutationOptions<
      TerminateTradingChallengeParticipationStatus200,
      ResponseErrorConfig<
        | TerminateTradingChallengeParticipationStatus403
        | TerminateTradingChallengeParticipationStatus404
        | TerminateTradingChallengeParticipationStatus409
        | TerminateTradingChallengeParticipationStatus500
      >,
      TerminateTradingChallengeParticipationOptions,
      TContext
    > & { client?: QueryClient }
    client?: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>>
  } = {},
) {
  const { mutation = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...mutationOptions } = mutation
  const mutationKey =
    mutationOptions.mutationKey ?? terminateTradingChallengeParticipationMutationKey()

  const baseOptions = terminateTradingChallengeParticipationMutationOptions(
    config,
  ) as UseMutationOptions<
    TerminateTradingChallengeParticipationStatus200,
    ResponseErrorConfig<
      | TerminateTradingChallengeParticipationStatus403
      | TerminateTradingChallengeParticipationStatus404
      | TerminateTradingChallengeParticipationStatus409
      | TerminateTradingChallengeParticipationStatus500
    >,
    TerminateTradingChallengeParticipationOptions,
    TContext
  >

  return useMutation<
    TerminateTradingChallengeParticipationStatus200,
    ResponseErrorConfig<
      | TerminateTradingChallengeParticipationStatus403
      | TerminateTradingChallengeParticipationStatus404
      | TerminateTradingChallengeParticipationStatus409
      | TerminateTradingChallengeParticipationStatus500
    >,
    TerminateTradingChallengeParticipationOptions,
    TContext
  >(
    {
      ...baseOptions,
      mutationKey,
      ...mutationOptions,
    },
    queryClient,
  ) as UseMutationResult<
    TerminateTradingChallengeParticipationStatus200,
    ResponseErrorConfig<
      | TerminateTradingChallengeParticipationStatus403
      | TerminateTradingChallengeParticipationStatus404
      | TerminateTradingChallengeParticipationStatus409
      | TerminateTradingChallengeParticipationStatus500
    >,
    TerminateTradingChallengeParticipationOptions,
    TContext
  >
}
